import type { WorkerSubmission } from '@rendimees24/shared-types';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getMetadata, ref, uploadBytes } from 'firebase/storage';
import { useEffect, useState } from 'react';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';

export type SubmissionInput = Pick<WorkerSubmission, 'id' | 'jobId' | 'workDate' | 'kind'> &
  Partial<
    Pick<
      WorkerSubmission,
      | 'timesheetId'
      | 'label'
      | 'quantity'
      | 'requestedByName'
      | 'notes'
      | 'purchaseDate'
      | 'payer'
      | 'purchaseAmountEur'
      | 'missingReceiptReason'
    >
  > & { attachmentId?: string; expectedRevision?: number; workerId?: string };
export async function submitWorkerSubmission(input: SubmissionInput, file?: File) {
  const { auth, storage, functions } = getFirebase();
  if (!input.workerId || input.workerId !== auth.currentUser?.uid)
    throw {
      code: 'functions/permission-denied',
      message: 'Submission belongs to another worker session',
    };
  if (file) {
    if (!auth.currentUser || !input.attachmentId) throw new Error('Missing upload identity');
    const object = ref(
      storage,
      `worker-submissions/${auth.currentUser.uid}/${input.id}/${input.attachmentId}`,
    );
    try {
      await getMetadata(object);
    } catch (e) {
      if ((e as { code?: string }).code !== 'storage/object-not-found') throw e;
      await uploadBytes(object, file, { contentType: file.type });
    }
  }
  return (
    await httpsCallable<SubmissionInput, { ok: boolean; id: string }>(
      functions,
      'submitWorkerSubmission',
    )(input)
  ).data;
}
export function useWorkerSubmissions(filter: { workerId?: string; jobId?: string }) {
  const [state, setState] = useState<{
    key: string;
    rows: WorkerSubmission[];
    error: Error | null;
  } | null>(null);
  const key = `${filter.workerId ?? ''}:${filter.jobId ?? ''}`;
  useEffect(() => {
    if (!filter.workerId && !filter.jobId) return;
    const constraints = filter.workerId
      ? [where('workerId', '==', filter.workerId)]
      : [where('jobId', '==', filter.jobId)];
    let live = true;
    const unsub = onSnapshot(
      query(collection(getFirebase().firestore, 'workerSubmissions'), ...constraints),
      (s) => {
        if (live)
          setState({
            key,
            rows: s.docs
              .map((d) => ({ ...d.data(), id: d.id }) as WorkerSubmission)
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
            error: null,
          });
      },
      (error) => {
        if (live) setState({ key, rows: [], error });
      },
    );
    return () => {
      live = false;
      unsub();
    };
  }, [filter.workerId, filter.jobId, key]);
  const current = state?.key === key ? state : null;
  return {
    rows: (current?.rows ?? []).filter((r) => !filter.jobId || r.jobId === filter.jobId),
    loading: !current,
    error: current?.error ?? null,
  };
}
export async function reviewWorkerSubmission(input: {
  id: string;
  expectedRevision: number;
  decision: 'accepted' | 'rejected' | 'needs_info';
  note?: string;
  reimburseEur?: number;
  offer?: { label: string; revenueEur: number; costEur: number; vatIncluded: boolean };
}) {
  return httpsCallable(getFirebase().functions, 'reviewWorkerSubmission')(input);
}
export async function confirmWorkerReimbursement(input: { id: string; reference: string }) {
  return httpsCallable(getFirebase().functions, 'confirmWorkerReimbursement')(input);
}
